<i18n>
  {
    "fr": {
      "title": "Horaires",
      "departure": "Départ de",
      "arrival": "Arrivée à",
      "errors": {
        "one-hour-delta": "Un intervalle d'une heure minimum est requis"
      }
    },
    "en": {
      "title": "Schedules",
      "departure": "Departure from",
      "arrival": "Arrival in",
      "errors": {
        "one-hour-delta": "An interval of at least one hour is required"
      }
    }
  }
</i18n>

<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    min-width="360px"
  >
    <template #activator="{ on, attrs, value }">
      <v-btn
        v-bind="attrs"
        depressed
        :color="isDefaultValue ? 'white' : 'primaryLight'"
        :class="{'filter-not-default-state': !isDefaultValue}"
        class="pr-2"
        v-on="on"
      >
        {{ $t('title') }}
        <v-icon
          v-if="value === true"
          color="primary"
        >
          mdi-menu-up
        </v-icon>
        <v-icon
          v-else-if="value === false"
          color="primary"
        >
          mdi-menu-down
        </v-icon>
      </v-btn>
    </template>

    <v-card flat>
      <v-card-title
        class="py-0"
      >
        <v-col>
          {{ $t('title') }}
        </v-col>
        <v-spacer />
        <v-btn
          icon
          @click="menu = false"
        >
          <v-icon color="primary">
            mdi-close
          </v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <div class="text-overline">
          <v-icon
            class="mr-3"
            small
          >
            mdi-airplane-takeoff
          </v-icon>&nbsp;
          <span>{{ outwardFlightDepartureAirportIata }}:</span>&nbsp;
          <span class="hour-accent">{{ formatThumbLabel(outwardFlightDepartureDelta[0]) }}-{{ formatThumbLabel(outwardFlightDepartureDelta[1]) }}</span>
        </div>
        <v-row>
          <v-col>
            <v-range-slider
              v-model="outwardFlightDepartureDelta"
              thumb-label
              thumb-size="48"
              :min="0"
              :max="24"
              :error-messages="outwardFlightDepartureDeltaErrors"
            >
              <template #thumb-label="props">
                {{ formatThumbLabel(props.value) }}
              </template>
            </v-range-slider>
          </v-col>
        </v-row>
        <div v-if="travelTypeState === 'two-way'">
          <div class="text-overline">
            <v-icon
              class="mr-3"
              small
            >
              mdi-airplane-takeoff
            </v-icon>
            <span>{{ returnFlightDepartureAirportIata }}:</span>&nbsp;
            <span class="hour-accent">{{ formatThumbLabel(returnFlightDepartureDelta[0]) }}-{{ formatThumbLabel(returnFlightDepartureDelta[1]) }}</span>
          </div>
          <v-row>
            <v-col>
              <v-range-slider
                v-model="returnFlightDepartureDelta"
                thumb-label
                thumb-size="48"
                :min="0"
                :max="24"
                :error-messages="returnFlightDepartureDeltaErrors"
              >
                <template #thumb-label="props">
                  {{ formatThumbLabel(props.value) }}
                </template>
              </v-range-slider>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script>
import _ from 'lodash'
import { mapMutations, mapGetters, mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { oneHourDeltaValidator } from '../../../../validators/oneHourDeltaValidator'

export default {
  name: 'FlightFilterSchedules',
  mixins: [validationMixin],
  data: () => ({
    menu: false
  }),
  computed: {
    ...mapState({
      travelTypeState: state => state.fb.searchResults.travelType,
      outwardFlightDepartureDeltaState: state => state.fb.searchFilters.outwardFlightDepartureDelta,
      returnFlightDepartureDeltaState: state => state.fb.searchFilters.returnFlightDepartureDelta
    }),
    ...mapGetters({
      getOutwardFlightDepartureAirport: 'fb/searchResults/getOutwardFlightDepartureAirportIata',
      getReturnFlightDepartureAirport: 'fb/searchResults/getReturnFlightDepartureAirportIata'
    }),
    outwardFlightDepartureDelta: {
      /**
       * Parse outward flight departure state and return an array whom can be used by the vuetify v-range-slider
       * @return {Array}
       */
      get () {
        const hourDelta = this.outwardFlightDepartureDeltaState
        const hourArray = []
        hourArray[0] = hourDelta.begin
        hourArray[1] = hourDelta.end
        return hourArray
      },
      /**
       * Parse the newVal array parameter to an object and save it to the vueX store
       * @param newVal {Array}
       */
      set (newVal) {
        const newDelta = {
          begin: newVal[0],
          end: newVal[1]
        }
        this.changeOutwardFlightDepartureTimeDelta(newDelta)
      }
    },
    /**
     * Create a sentence with i18n and airport iata code
     * @return {string}
     */
    outwardFlightDepartureAirportIata () {
      const iataCode = this.getOutwardFlightDepartureAirport
      const traduction = this.$i18n.t('departure')
      return `${traduction} ${iataCode}`
    },
    /**
     * Return an array filled with errors based on vuelidate states
     * @return {string[]}
     */
    outwardFlightDepartureDeltaErrors () {
      const errors = []
      const oneHourDeltaError = this.$i18n.t('errors.one-hour-delta')
      !this.$v.outwardFlightDepartureDelta.oneHourDeltaValidator && errors.push(oneHourDeltaError)
      return errors
    },
    returnFlightDepartureDelta: {
      /**
       * Parse return flight departure state and return an array whom can be used by the vuetify v-range-slider
       * @return {Array}
       */
      get () {
        const hourDelta = this.returnFlightDepartureDeltaState
        const hourArray = []
        hourArray[0] = hourDelta.begin
        hourArray[1] = hourDelta.end
        return hourArray
      },
      /**
       * Parse the newVal array parameter to an object and save it to the vueX store
       * @param newVal {Array}
       */
      set (newVal) {
        const newDelta = {
          begin: newVal[0],
          end: newVal[1]
        }
        this.changeReturnFlightDepartureTimeDelta(newDelta)
      }
    },
    /**
     * Create a sentence with i18n and airport iata code
     * @return {string}
     */
    returnFlightDepartureAirportIata () {
      const iataCode = this.getReturnFlightDepartureAirport
      const traduction = this.$i18n.t('departure')
      return `${traduction} ${iataCode}`
    },
    /**
     * Return an array filled with errors based on vuelidate states
     * @return {string[]}
     */
    returnFlightDepartureDeltaErrors () {
      const errors = []
      const oneHourDeltaError = this.$i18n.t('errors.one-hour-delta')
      !this.$v.returnFlightDepartureDelta.oneHourDeltaValidator && errors.push(oneHourDeltaError)
      return errors
    },
    /**
     * Return a boolean based on the default states of the component (true if all the states have default values)
     * @return {boolean}
     */
    isDefaultValue () {
      return !(this.outwardFlightDepartureDelta[0] !== 0 ||
        this.outwardFlightDepartureDelta[1] !== 24 ||
        this.returnFlightDepartureDelta[0] !== 0 ||
        this.returnFlightDepartureDelta[1] !== 24)
    },
    /**
     * This value need to be computed for the watcher to work properly
     * @return {boolean}
     */
    isValid () {
      return !this.$v.$invalid
    }
  },
  watch: {
    /**
     * Modify the validity of the component in the store
     * @param newVal {boolean}
     */
    isValid (newVal) {
      this.changeValidity(newVal)
    }
  },
  created () {
    this.changeOutwardFlightDepartureTimeDelta = _.debounce(this.changeOutwardFlightDepartureTimeDelta, 600)
    this.changeReturnFlightDepartureTimeDelta = _.debounce(this.changeReturnFlightDepartureTimeDelta, 600)
  },
  validations: {
    outwardFlightDepartureDelta: {
      oneHourDeltaValidator
    },
    returnFlightDepartureDelta: {
      oneHourDeltaValidator
    }
  },
  methods: {
    ...mapMutations({
      changeOutwardFlightDepartureTimeDelta: 'fb/searchFilters/setOutwardFlightDepartureDelta',
      changeReturnFlightDepartureTimeDelta: 'fb/searchFilters/setReturnFlightDepartureDelta',
      changeValidity: 'fb/searchFilters/setSchedulesFilterValidity'
    }),
    /**
     * Return either a string or null, format number value to time with HH H mm format
     * @param value {number}
     * @return {string|null}
     */
    formatThumbLabel (value) {
      if (value < 0) {
        return null
      } else if (value < 10) {
        return `0${value}H00`
      } else if (value >= 10 && value < 24) {
        return `${value}H00`
      } else if (value === 24) {
        return '23H59'
      } else {
        return null
      }
    }
  }
}
</script>

<style scoped>
  .hour-accent {
    color: #00bfff;
  }

  .filter-not-default-state {
    color: #003d58;
  }
</style>
